import { useState, useRef, useCallback, useEffect } from 'react';
import { loadPdf, extractPdfPageText } from './pdfTextExtractor';

// Calls Google Gemini directly from the client — same key/model/pattern used
// by the mobile app (layos-mobile/src/services/ai-tutor.ts, app/(student)/ai-search.tsx).
// No backend proxy, no credit/usage tracking. Replaces the old `/api/chat`
// endpoint this hook used to call, which was never actually implemented server-side.
const GEMINI_MODELS = ['gemini-3.6-flash', 'gemini-2.5-flash', 'gemini-2.0-flash'];

const callGemini = async (systemInstruction: string, userText: string) => {
    const apiKey = import.meta.env.VITE_GEMINI_API_KEY || 'AQ.Ab8RN6LWitYCoNDopPmFDw9eVjF06E-VUKcYwj-pGAYFIlHT2g';
    if (!apiKey) {
        throw new Error('Missing Gemini API key in environment variables.');
    }

    const requestBody = JSON.stringify({
        systemInstruction: { parts: [{ text: systemInstruction }] },
        contents: [{ role: 'user', parts: [{ text: userText }] }],
        generationConfig: {
            temperature: 0.2,
            maxOutputTokens: 4096,
        },
    });

    let lastError: any = null;

    for (const model of GEMINI_MODELS) {
        try {
            const url = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${apiKey}`;
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: requestBody,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                lastError = errorData;
                continue;
            }

            const data = await response.json();
            const text = data.candidates?.[0]?.content?.parts?.[0]?.text;
            if (text) {
                return text as string;
            }
        } catch (err) {
            lastError = err;
        }
    }

    console.error('All Gemini models failed in useAIPutter:', lastError);
    throw new Error(`AI Tutor busy: ${lastError?.error?.message || 'Check connection'}`);
};

export type AIState = 'idle' | 'extracting' | 'summarizing' | 'ready' | 'speaking' | 'paused' | 'error';

interface AIExplanation {
    title: string;
    content: string;
    page: number;
}

export const useAIPutter = () => {
    const [state, setState] = useState<AIState>('idle');
    const [error, setError] = useState<string | null>(null);
    const [fullText, setFullText] = useState<string>('');
    const [explanations, setExplanations] = useState<AIExplanation[]>([]);
    const [currentExplanationIndex, setCurrentExplanationIndex] = useState(-1);
    const [isSpeaking, setIsSpeaking] = useState(false);

    const explanationsRef = useRef<AIExplanation[]>([]);
    const currentSentenceIndexRef = useRef(0);
    const isChattingRef = useRef(false);
    const isSpeakingRef = useRef(false);
    const currentRunIdRef = useRef(0);

    // Ensure voices are loaded for some browsers
    useEffect(() => {
        window.speechSynthesis.getVoices();
        const handleVoicesChanged = () => window.speechSynthesis.getVoices();
        window.speechSynthesis.addEventListener('voiceschanged', handleVoicesChanged);
        return () => window.speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged);
    }, []);

    const cleanTextForSpeech = (text: string) => {
        return text
            .replace(/\[PAGE\s+\d+\]/gi, '')
            .replace(/[#*_~`\-]/g, ' ') // Added dash (-) to the strip list
            .replace(/\s+/g, ' ')
            .trim();
    };

    const stop = useCallback(() => {
        window.speechSynthesis.resume();
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
        isSpeakingRef.current = false;
        setState('idle');
        currentSentenceIndexRef.current = 0;
    }, []);

    const pause = useCallback(() => {
        window.speechSynthesis.pause();
        setIsSpeaking(false);
        isSpeakingRef.current = false;
        setState('paused');
    }, []);

    const playExplanationAudio = (index: number, sentenceIndex = 0) => {
        if (index < 0 || index >= explanationsRef.current.length) return;

        setState('speaking');
        setIsSpeaking(true);
        isSpeakingRef.current = true;
        setCurrentExplanationIndex(index);
        currentSentenceIndexRef.current = sentenceIndex;

        const explanation = explanationsRef.current[index];
        const cleanContent = cleanTextForSpeech(explanation.content);
        const sentences = cleanContent.split(/(?<=[.!?])\s+/).filter(s => s.trim().length > 0);

        if (sentenceIndex >= sentences.length) {
            const nextIndex = index + 1;
            if (nextIndex < explanationsRef.current.length) {
                playExplanationAudio(nextIndex, 0);
            } else {
                setState('idle');
                setIsSpeaking(false);
                isSpeakingRef.current = false;
            }
            return;
        }

        try {
            window.speechSynthesis.resume();
            window.speechSynthesis.cancel();
            const utterance = new SpeechSynthesisUtterance(sentences[sentenceIndex].trim());
            const voices = window.speechSynthesis.getVoices();
            const femaleVoice = voices.find(v =>
                (v.name.includes('Female') || v.name.includes('Aria') || v.name.includes('Zira') || v.name.includes('Samantha')) &&
                v.lang.includes('en')
            );

            if (femaleVoice) utterance.voice = femaleVoice;
            utterance.rate = 1.08;

            utterance.onend = () => {
                if (!isChattingRef.current && isSpeakingRef.current) {
                    playExplanationAudio(index, sentenceIndex + 1);
                }
            };

            utterance.onerror = (e) => {
                console.error('[Virtual Tutor] Utterance Error:', e);
                setTimeout(() => {
                    if (isSpeakingRef.current) playExplanationAudio(index, sentenceIndex + 1);
                }, 500);
            };

            window.speechSynthesis.speak(utterance);
        } catch (err) {
            console.error('[Virtual Tutor] TTS Failure:', err);
        }
    };

    const resume = useCallback(() => {
        if (window.speechSynthesis.paused) {
            window.speechSynthesis.resume();
            setIsSpeaking(true);
            isSpeakingRef.current = true;
            setState('speaking');
        } else {
            playExplanationAudio(currentExplanationIndex, currentSentenceIndexRef.current);
        }
    }, [currentExplanationIndex]);

    const speakChatAnswer = (text: string) => {
        try {
            isChattingRef.current = true;
            window.speechSynthesis.resume();
            window.speechSynthesis.cancel();
            setIsSpeaking(true);
            setState('speaking');

            const cleanText = cleanTextForSpeech(text);
            const utterance = new SpeechSynthesisUtterance(cleanText);
            const voices = window.speechSynthesis.getVoices();
            const femaleVoice = voices.find(v =>
                (v.name.includes('Female') || v.name.includes('Aria') || v.name.includes('Zira') || v.name.includes('Samantha')) &&
                v.lang.includes('en')
            );
            if (femaleVoice) utterance.voice = femaleVoice;
            utterance.rate = 1.05;

            utterance.onend = () => {
                isChattingRef.current = false;
                setIsSpeaking(false);
                setTimeout(() => {
                    if (state !== 'paused' && state !== 'idle' && currentExplanationIndex >= 0) {
                        playExplanationAudio(currentExplanationIndex, currentSentenceIndexRef.current);
                    } else if (currentExplanationIndex < 0) {
                        setState('idle');
                    }
                }, 1000);
            };

            window.speechSynthesis.speak(utterance);
        } catch (err) {
            console.error('[Virtual Tutor] Chat TTS Failure:', err);
            isChattingRef.current = false;
        }
    };

    const startAIIntelligence = async (pdfUrl: string) => {
        stop();
        setState('extracting');
        setError(null);

        const runId = ++currentRunIdRef.current;

        try {
            const pdf = await loadPdf(pdfUrl);
            if (runId !== currentRunIdRef.current) return;
            const totalPages = pdf.numPages;
            const chunkSize = 5; // Increased chunk size to speed up sync and avoid 429 errors
            let accumulatedFullText = '';
            let accumulatedExplanations: AIExplanation[] = [];

            setState('summarizing');

            for (let startPage = 1; startPage <= totalPages; startPage += chunkSize) {
                if (runId !== currentRunIdRef.current) return;

                const endPage = Math.min(startPage + chunkSize - 1, totalPages);

                const pagePromises = [];
                for (let i = startPage; i <= endPage; i++) {
                    pagePromises.push(
                        extractPdfPageText(pdf, i).then(pageText => {
                            const safeText = pageText.substring(0, 3000); // More text per page
                            return `[PAGE ${i} START]\n${safeText}\n[PAGE ${i} END]\n\n`;
                        })
                    );
                }
                const pagesContent = await Promise.all(pagePromises);
                const chunkText = pagesContent.join('');
                accumulatedFullText += chunkText;

                const systemInstruction = "You are a Virtual Tutor creating detailed, educational lesson sections from document pages. Maintain high factual accuracy.";
                const prompt = `CRITICAL GOAL: Create detailed lesson sections for pages ${startPage} to ${endPage} of a ${totalPages}-page document.

                Instructional Guidelines:
                1. DETAIL: Provide a rich, educational summary for EACH page in this chunk.
                2. FORMAT: Every page MUST use this EXACT format: SECTION TITLE: [Topic] | PAGE: [Int] | CONTENT: [Detailed Explanation]
                3. FIDELITY: Maintain high factual accuracy.

                Document Content (Pages ${startPage}-${endPage}):
                ${chunkText}`;

                const rawResponse = await callGemini(systemInstruction, prompt);

                if (runId !== currentRunIdRef.current) return;

                const sections = rawResponse.split(/SECTION TITLE:/i).filter((s: string) => s.trim().length > 0);
                const chunkExplanations: AIExplanation[] = sections.map((s: string) => {
                    const parts = s.split(/\||CONTENT:/i);
                    const title = parts[0]?.trim() || 'Concept Summary';
                    
                    // Robustly extract the page number using regex
                    const pageMatch = s.match(/PAGE\s*:?\s*(\d+)/i);
                    let pageNum = startPage;
                    if (pageMatch && pageMatch[1]) {
                        pageNum = parseInt(pageMatch[1], 10);
                    }
                    
                    // Robustly extract content
                    let content = s;
                    const contentMatch = s.match(/CONTENT:\s*([\s\S]*)/i);
                    if (contentMatch && contentMatch[1]) {
                        content = contentMatch[1].trim();
                    } else if (parts.length > 1) {
                        content = parts[parts.length - 1].trim();
                    }

                    return { title, content, page: pageNum };
                });

                accumulatedExplanations = [...accumulatedExplanations, ...chunkExplanations];

                // Incremental update so the user sees progress and can start listening
                setExplanations([...accumulatedExplanations]);
                explanationsRef.current = [...accumulatedExplanations];
                setFullText(accumulatedFullText);
            }

            if (runId === currentRunIdRef.current) {
                setState('ready');
            }

        } catch (err: any) {
            if (runId !== currentRunIdRef.current) return;
            console.error('Core Intelligence failure:', err);
            setError(err.message || 'Brain connection lost.');
            setState('error');
        }
    };

    const askQuestion = async (question: string) => {
        try {
            const answer = await callGemini(
                "You are the Layos Virtual Tutor. Answer clearly based on document context.",
                `Context: ${fullText.substring(0, 5000)}\n\nQuestion: ${question}`
            );
            speakChatAnswer(answer);
            return answer;
        } catch {
            return "I apologize, I'm having trouble thinking right now.";
        }
    };

    return {
        state,
        error,
        explanations,
        currentExplanationIndex,
        isSpeaking,
        startAIIntelligence,
        startSpeaking: () => playExplanationAudio(0, 0),
        stop,
        pause,
        resume,
        askQuestion,
        speakText: speakChatAnswer
    };
};
